import React, { useState, useEffect, useRef } from 'react';
import { db, auth, collection, getDocs, doc, getDoc } from '../../Services/firebase-config';
import {
  onAuthStateChanged
} from 'firebase/auth'; // Import onAuthStateChanged
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
  PieChart,
  Pie
} from 'recharts';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const COST_AMOUNT_LABEL = "Cost Amount";
const COST_CATEGORY_LABEL = "Cost Category";

// Helper for colors in Pie charts if needed
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const DashboardContent = () => {
  const [userUid, setUserUid] = useState(null);
  // Existing product/financial states
  const [products, setProducts] = useState([]);
  const [kibalos, setKibalos] = useState([]);
  const [kibaloCount, setKibaloCount] = useState(0);

  // New states for additional data
  const [allStrategies, setAllStrategies] = useState([]);
  const [allChallenges, setAllChallenges] = useState([]);
  const [allActors, setAllActors] = useState([]);
  const [leastProfitableProduct, setLeastProfitableProduct] = useState(null);


  // New state for selections
  const [selectedKibaloId, setSelectedKibaloId] = useState(''); // 'all' or a specific kibalo ID, default to none
  const [productsInSelectedKibalo, setProductsInSelectedKibalo] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('all'); // 'all' or a specific product ID
  const [cumulativeProfitData, setCumulativeProfitData] = useState([]);
  const [costCategoryData, setCostCategoryData] = useState([]);
  const [notification, setNotification] = useState({ show: false, message: '' });

  const chartsRef = useRef([]);

  const resetState = () => {
    setProducts([]);
    setKibalos([]);
    setKibaloCount(0);
    setAllStrategies([]);
    setAllChallenges([]);
    setAllActors([]);
    setLeastProfitableProduct(null);
    setSelectedKibaloId('');
    setProductsInSelectedKibalo([]);
    setSelectedProductId('all');
    setCumulativeProfitData([]);
    setCostCategoryData([]);
    setNotification({ show: false, message: '' });
  };

  const showNotification = (message) => {
    setNotification({ show: true, message });
    // Hide the notification after 3 seconds
    setTimeout(() => setNotification({ show: false, message: '' }), 3000);
  };

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserUid(user.uid);
      } else {
        setUserUid(null);
        resetState();
      }
    });
    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!userUid) {
        resetState();
        return;
      }

      try {
        const kibalosSnapshot = await getDocs(collection(db, 'users', userUid, 'kibalos'));
        const userKibalos = [];
        kibalosSnapshot.forEach((vcDoc) => { // vcDoc is the DocumentSnapshot
          userKibalos.push({ id: vcDoc.id, ...vcDoc.data() }); // Store the full data from the kibalo document
        });
        setKibalos(userKibalos);
        setKibaloCount(userKibalos.length);

        let allProductsData = [];
        let tempStrategies = [];
        let tempChallenges = [];
        let tempActors = [];

        for (const vc of userKibalos) {
          // Fetch Products (Financial)
          const productsRef = collection(db, 'users', userUid, 'kibalos', vc.id, 'products');
          // Fetch all products and sort on the client-side to handle older data without an 'order' field.
          const productsSnapshot = await getDocs(productsRef);
          const vcProducts = productsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
            .sort((a, b) => {
              const orderA = a.order || a.createdAt?.toMillis() || 0;
              const orderB = b.order || b.createdAt?.toMillis() || 0;
              return orderA - orderB;
            });

          vcProducts.forEach((data) => {
            // const data = prodDoc.data(); // No longer needed, data is the iterated item

            const unitPrice = Number(data.unitPrice) || 0;
            const unitCostPerAcre = Number(data.unitCostPerAcre) || 0;
            const rawConversionRate = data.conversionRate; 

            // If conversionRate is not defined (e.g., older product), default to 100% (factor 1.0)
            // Otherwise, use the stored percentage value divided by 100.
            const conversionFactor = typeof rawConversionRate === 'number' ? rawConversionRate / 100 : 1.0;
            
            // Calculate the grand total from by-products
            const byProductsGrandTotal = (data.byProducts || []).reduce((acc, byProduct) => {
              const rmq = parseFloat(byProduct.rawMaterialQuantity) || 0;
              const yieldPercent = parseFloat(byProduct.yield) || 0;
              const bpq = rmq * (yieldPercent / 100);
              const price = parseFloat(byProduct.pricePerUnit) || 0;
              return acc + (bpq * price);
            }, 0);
            
            const calculatedProfit = (unitPrice - unitCostPerAcre) + byProductsGrandTotal;
            
            allProductsData.push({
              id: data.id, kibaloId: vc.id, name: data.productName || 'Unnamed Product',
              unitPrice: unitPrice, unitCostPerAcre: unitCostPerAcre, // This is the value from Firestore
              profit: calculatedProfit, // Use the dynamically calculated profit
              conversionRate: rawConversionRate, // Keep for reference if needed 
              variableCosts: data.variableCosts || [],
              // The data from the kibalo document needs to be added here
              establishmentTable: vc.establishmentTable || [],
              depreciationTable: vc.depreciationTable || [],
              maintenanceTable: vc.maintenanceTable || [],
              interestTable: vc.interestTable || [],
              // These are not directly on product, but let's check if they are on kibalo
              maintenanceCosts: vc.maintenanceTable || [], // Assuming maintenanceCosts is maintenanceTable
              landCosts: vc.landCosts || [],
              labourCosts: vc.labourCosts || [],
              // variableCosts are per-product, so what's already there is correct.

            });
          });

          // Fetch Strategies
          const strategiesSnapshot = await getDocs(collection(db, 'users', userUid, 'kibalos', vc.id, 'strategies'));
          strategiesSnapshot.forEach(stratDoc => {
            tempStrategies.push({ id: stratDoc.id, kibaloId: vc.id, ...stratDoc.data() });
          });

          // Fetch Challenges
          const challengesSnapshot = await getDocs(collection(db, 'users', userUid, 'kibalos', vc.id, 'challenges'));
          challengesSnapshot.forEach(challDoc => {
            tempChallenges.push({ id: challDoc.id, kibaloId: vc.id, ...challDoc.data() });
          });

          // Fetch Actors
          const actorsSnapshot = await getDocs(collection(db, 'users', userUid, 'kibalos', vc.id, 'actors'));
          actorsSnapshot.forEach(actorDoc => {
            tempActors.push({ id: actorDoc.id, valueChainId: vc.id, ...actorDoc.data() });
          });
        }
        setProducts(allProductsData);
        setAllStrategies(tempStrategies);
        setAllChallenges(tempChallenges);
        setAllActors(tempActors);

        if (selectedKibaloId !== 'all' && selectedKibaloId !== '') {
          setProductsInSelectedKibalo(allProductsData.filter(p => p.kibaloId === selectedKibaloId));
        } else {
          setProductsInSelectedKibalo([]);
        }

      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [userUid]);

  useEffect(() => {
    if (selectedKibaloId === 'all' || selectedKibaloId === '') {
      setProductsInSelectedKibalo([]);
      setSelectedProductId('all');
    } else {
      setProductsInSelectedKibalo(products.filter(p => p.kibaloId === selectedKibaloId));
      setSelectedProductId('all');
    }
  }, [selectedKibaloId, products]);

  useEffect(() => {
    const displayProducts = getDisplayProducts(products, selectedKibaloId, selectedProductId);
    if (selectedKibaloId && selectedKibaloId !== 'all') {
      const selectedKibalo = kibalos.find(k => k.id === selectedKibaloId);
      if (!selectedKibalo) return;
      
      const costs = {
        'Establishment': selectedKibalo.establishmentCostPerYear || 0,
        'Depreciation': selectedKibalo.depreciationTable?.reduce((acc, item) => acc + parseFloat(item.depreciationAmount || '0'), 0) || 0,
        'Maintenance': selectedKibalo.maintenanceTable?.reduce((acc, item) => acc + parseFloat(item.amount || '0'), 0) || 0,
        'Interest': selectedKibalo.interestTable?.reduce((acc, item) => acc + parseFloat(item.amount || '0'), 0) || 0,
        'Variable': selectedKibalo.variableCosts?.reduce((acc, item) => acc + parseFloat(item.amount || '0'), 0) || 0,
      };

      const chartData = Object.entries(costs).map(([name, value]) => ({ name, Amount: value }));
      setCostCategoryData(chartData);
    } else {
      // If 'All Kibalos' is selected, or no kibalo, clear the data.
      setCostCategoryData([]);
    }

    setCumulativeProfitData(getCumulativeData(displayProducts, 'profit'));
  }, [products, selectedKibaloId, kibalos]);

  const getDisplayProducts = (allProds, vcId, prodId) => {
    if (vcId === '') return []; // Don't show anything if no kibalo is selected

    if (prodId !== 'all' && prodId !== '') {
      return allProds.filter(p => p.id === prodId && (vcId === 'all' || p.kibaloId === vcId));
    }
    if (vcId !== 'all' && vcId !== '') {
      return allProds.filter(p => p.kibaloId === vcId);
    }
    return allProds;
  };

  const getFilteredData = (dataArray, vcId) => {
    if (vcId === '') {
        return [];
    }
    if (vcId === 'all') {
      return dataArray;
    }
    return dataArray.filter(item => item.kibaloId === vcId);
  };

  const getCumulativeData = (data, key) => {
    let cumulative = 0;
    return data.map((item) => {
      cumulative += item[key] || 0;
      return { ...item, cumulative };
    });
  };

  const copyChart = async (index) => {
    const chartElement = chartsRef.current[index];
    if (!chartElement) return;
    try {
      const canvas = await html2canvas(chartElement);
      canvas.toBlob(async (blob) => {
        if (!blob) {
          showNotification('Failed to create chart image.');
          return;
        }
        try {
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
          showNotification('Chart copied to clipboard!');
        } catch (copyError) {
          console.error('Clipboard write failed: ', copyError);
          showNotification('Could not copy. Please try again or save the chart as a PDF.');
        }
      });
    } catch (error) {
      console.error('Error generating chart image:', error);
      showNotification('Failed to generate chart image.');
    }
  };

  const printAllCharts = async () => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    let firstPage = true;
    // Adjust the loop to include all charts/sections you want in the PDF
    // Currently, chartsRef.current will have elements for indices 0-5 (charts) and 6 (the list div)
    // html2canvas can capture the list div.
    for (let i = 0; i < 6; i++) { // Iterate up to 6 elements (0 to 5)
      const chart = chartsRef.current[i];
      if (!chart) continue;
      try {
        const canvas = await html2canvas(chart);
        const imgData = canvas.toDataURL('image/png');
        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth() - 20;
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        if (!firstPage) {
          pdf.addPage();
        }
        pdf.addImage(imgData, 'PNG', 10, 10, pdfWidth, pdfHeight);
        firstPage = false;
      } catch (error) {
        console.error(`Error adding element ${i} to PDF:`, error);
      }
    }
    pdf.save('dashboard_charts.pdf');
  };

  const displayProducts = getDisplayProducts(products, selectedKibaloId, selectedProductId);

  const displayedProductCount = selectedKibaloId === ''
    ? 0
    : selectedKibaloId === 'all'
    ? products.length
    : products.filter(p => p.kibaloId === selectedKibaloId).length;

  const selectedKibaloName = selectedKibaloId === 'all' ? 'All Kibalos' : kibalos.find(vc => vc.id === selectedKibaloId)?.name;

  const filteredStrategies = getFilteredData(allStrategies, selectedKibaloId);
  const top5StrategiesData = [...filteredStrategies]
    .sort((a, b) => (parseFloat(b.impactGDP) || 0) - (parseFloat(a.impactGDP) || 0))
    .slice(0, 5)
    .map(s => ({ name: s.strategyIdea, value: parseFloat(s.impactGDP) || 0 }));

  const filteredChallenges = getFilteredData(allChallenges, selectedKibaloId);
  const challengesChartData = filteredChallenges.map(c => ({
    name: c.challenge,
    value: parseInt(c.weightedScore, 10) || 0
  })).sort((a,b) => b.value - a.value)
    .slice(0, 5);

  const filteredActors = getFilteredData(allActors, selectedKibaloId);
  const actorsChartData = filteredActors.map(a => ({
    name: a.stakeholder,
    value: parseInt(a.weightedScore, 10) || 0
  })).sort((a,b) => b.value - a.value)
    .slice(0, 5);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
   <>
      {notification.show && (
        <div className="fixed top-5 right-5 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg transition-opacity duration-300">
          {notification.message}
        </div>
      )}

      <h1 className="text-2xl font-bold text-gray-800 mb-6">Dashboard</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white shadow-md rounded-lg p-4">
          <h2 className="text-lg font-semibold text-gray-700">
            Total Enterprises
          </h2>
          <p className="text-2xl font-bold text-gray-900">{kibaloCount}</p>
        </div>

        <div className="bg-white shadow-md rounded-lg p-4">
          <label htmlFor="kibaloSelect" className="block text-sm font-medium text-gray-700 mb-1">Select Enterprise</label>
          <select
            id="kibaloSelect"
            value={selectedKibaloId}
            onChange={(e) => setSelectedKibaloId(e.target.value)}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          >
            <option value="all">All Enterprises</option>
            {kibalos.map(vc => (
              <option key={vc.id} value={vc.id}>{vc.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div ref={(el) => (chartsRef.current[0] = el)} className="bg-white shadow-md rounded-lg p-6 mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-700">
            Cumulative Graph For {selectedProductId !== 'all' && displayProducts.length > 0 ? `for ${displayProducts[0]?.name || 'Selected Product'}` : selectedKibaloId !== 'all' ? ` Kibalo: ${kibalos.find(vc => vc.id === selectedKibaloId)?.name || ''}` : 'Over Products'}
          </h2>
          <button onClick={() => copyChart(0)} className="text-sm text-blue-600 hover:underline">Copy</button> 
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={costCategoryData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" /> 
            <XAxis dataKey="name" name={COST_CATEGORY_LABEL} />
            <YAxis /> 
            <Legend /> 
            <Line type="monotone" dataKey="Amount" stroke="#8884d8" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} name={COST_AMOUNT_LABEL}>
              <LabelList dataKey="Amount" position="top" />
            </Line>
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Removing the Unit Price, Cost, and New Money per Product graph */}
      {/* The following code block has been commented out to remove the graph from the dashboard */}
      {/*
      <div ref={(el) => (chartsRef.current[1] = el)} className="bg-white shadow-md rounded-lg p-6 mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-700">Unit Price, Cost, and New Money per Product</h2>
          <button onClick={() => copyChart(1)} className="text-sm text-blue-600 hover:underline">Copy</button>
        </div>
        <ResponsiveContainer width="100%" height={displayProducts.length * 60 + 60 > 400 ? displayProducts.length * 60 + 60 : 400}>
          <BarChart data={displayProducts} layout="vertical" margin={{ top: 20, right: 30, left: 120, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis dataKey="name" type="category" width={100} interval={0} />
            <Tooltip />
            <Legend />
            <Bar dataKey="unitPrice" fill="#8884d8" barSize={15} name="Unit Price" />
            <Bar dataKey="unitCostPerAcre" fill="#82ca9d" barSize={15} name="Unit Cost" />
            <Bar dataKey="profit" fill="#ffc658" barSize={15} name="Profit" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      */}

      <div ref={(el) => (chartsRef.current[1] = el)} className="bg-white shadow-md rounded-lg p-6 mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-700">
           Bar Graph For {selectedKibaloName || 'Selected Enterprise'}
          </h2>
          <button onClick={() => copyChart(1)} className="text-sm text-blue-600 hover:underline">Copy</button>
        </div> 
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={costCategoryData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" /> 
            <XAxis dataKey="name" name={COST_CATEGORY_LABEL} />
            <YAxis /> 
            <Legend /> 
            <Bar dataKey="Amount" fill="#ff7f50" name={COST_AMOUNT_LABEL}>
              <LabelList dataKey="Amount" position="top" />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div ref={(el) => (chartsRef.current[2] = el)} className="bg-white shadow-md rounded-lg p-6 mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-700">
            Input Cost Contribution for {selectedKibaloName || 'Selected Enterprise'}
          </h2>
          <button onClick={() => copyChart(2)} className="text-sm text-blue-600 hover:underline">Copy</button>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={costCategoryData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              outerRadius={100}
              fill="#8884d8"
              dataKey="Amount"
              nameKey="name"
            />
            <Tooltip formatter={(value, name) => [value.toLocaleString(), name]} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>

      
      
      <div className="flex justify-end">
        <button onClick={printAllCharts} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
          Print All Charts as PDF
        </button>
      </div>
      </>
    </div>
  );
};

export default DashboardContent;
